/* eslint-disable eqeqeq */
import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'

import _ from 'lodash'
import memoize from 'moize'

import { Container, Flex } from '@stackhouse/styled-elements'

import Button from '@stackhouse/dashboard-components/Button'
import Toast from '@stackhouse/dashboard-components/Toast'
import DropdownMenu from '@stackhouse/dashboard-components/DropdownMenu'

import DataGrid from '@stackhouse/dashboard-components/DataGrid'
import DataList from '@stackhouse/dashboard-components/DataList'
import template from '@stackhouse/dashboard-components/utils/template'

import { withDashboardContext, withRouter } from '@stackhouse/dashboard-components/Dashboard'

import GridLayout from '../modals/gridLayout'
import isMobile from '../utils/is-mobile'
import { getErrorMessage } from '../utils/api-error'


const gridConfigEndpoint = functionName => `/settings/GridConfig/${functionName}`
const gridLayoutEndpoint = functionName => `/settings/GridLayout/${functionName ? `${functionName}${isMobile ? 'Mobile' : ''}` : ''}`


const sortSelectProps = {
    height: 35,
    rounded: true,
    hideLabelPadding: true,
    containerProps: { flex: 1, padding: '5px 10px 10px 10px' },
    contentContainerProps: { flex: 1, padding: '5px 10px' }
}


class GridTemplate extends PureComponent {

    constructor(props) {
        super(props)
        this.gridRef = React.createRef()
        this.state = { grid: {}, layout: {}, rows: [] }
    }

    static propTypes = {
        functionName: PropTypes.string,
        rows: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.shape({
                elements: PropTypes.array,
                elementCount: PropTypes.number,
            }),
        ]),
        rowsEndpoint: PropTypes.string,
        editEndpoint: PropTypes.string,
        grid: PropTypes.object,
        layout: PropTypes.object,
        onClickRow: PropTypes.func,
        onDoubleClickRow: PropTypes.func,
        childPath: PropTypes.string,
        showSort: PropTypes.bool,
        showDataList: PropTypes.bool,
        title: PropTypes.string,
        actions: PropTypes.shape({
            grid: PropTypes.array,
        }),
    }

    static defaultProps = {
        showDataList: false
    }

    static forwardedMethods = [
        'loadData'
    ]

    componentDidMount() {
        return this.loadData()
    }


    static getDerivedStateFromProps(props, state) {
        if (typeof props.rows === 'object' && props.rows.elements && state.rows !== props.rows.elements) {
            const { rows } = props
            return {
                rows: Array.isArray(rows) ? rows : (rows.elements || []),
                total: rows.elementCount,
            }
        }
        return null
    }

    loadData = () => {
        const { withLoader, showDataList, rows, rowsEndpoint, client, grid, layout, functionName } = this.props
        return withLoader(done => {
            const _grid = grid ? grid instanceof Function ? grid() : grid : functionName ? client.get(gridConfigEndpoint(functionName)).then(res => res.data) : {}
            const _rows = rows ? rows instanceof Function ? rows() : rows : rowsEndpoint ? client.get(`${rowsEndpoint}`).then(res => res.data) : []
            const _layout = showDataList ? {} : layout ? layout instanceof Function ? layout() : layout : functionName ? client.get(gridLayoutEndpoint(functionName), { ignoreError: true }).then(res => res.data) : {}

            return Promise.all([_grid, _layout, _rows]).then(([grid, layout, rows]) => {
                const parsedLayout = layout && layout.layoutJson && JSON.parse(layout.layoutJson)
                return this.setState({
                    layout: parsedLayout || {},
                    rows: Array.isArray(rows) ? rows : (rows.elements || []),
                    config: grid,
                    total: rows.elementCount,
                    grid: this.processLayout(grid, parsedLayout)
                })
            }).then(done).catch(done)
        })
    }


    processLayout = (grid, customLayout) => {
        if (!customLayout || !Object.keys(customLayout).length) {
            return grid
        }

        const [{ columnsWidth, columnsOrder }] = this.gridRef.current.getState()
        const layout = { columnsWidth, columnsOrder, ...customLayout }

        const columns = grid.columns.reduce((acc, inc) => {

            const column = { ...inc }

            if (layout.customLabels && layout.customLabels[inc.key]) {
                column.label = layout.customLabels[inc.key]
            }

            if (layout.columnsWidth && layout.columnsWidth[inc.key]) {
                column.width = layout.columnsWidth[inc.key]
            }

            if (layout.hiddenColumns && layout.hiddenColumns.includes(inc.key)) {
                column.width = 0
            }

            return [...acc, column]
        }, [])


        const sortedColumns = !layout.columnsOrder ? columns : columns.sort((a, b) => {
            return layout.columnsOrder.indexOf(a.key) - layout.columnsOrder.indexOf(b.key)
        })

        return { ...grid, columns: sortedColumns }
    }

    applyLayout = (layout, shouldSave, close) => {
        return this.setState({ layout, grid: this.processLayout(this.state.config, layout) }, () => {

            if (shouldSave) {
                return this.saveLayout(close)
            }
            else {
                return close && close()
            }
        })
    }

    saveLayout = (callback = (() => { })) => {
        const { client, withLoader, functionName } = this.props

        const [{ columnsWidth, columnsOrder }] = this.gridRef.current.getState()
        const { hiddenColumns, customLabels } = this.state.layout || {}

        const layout = { columnsWidth, columnsOrder, hiddenColumns, customLabels }
        const body = { layoutCode: `${functionName}${isMobile ? 'Mobile' : ''}`, jsonLayoutConfig: JSON.stringify(layout) }

        return withLoader(done => {
            return client.post(gridLayoutEndpoint(''), body).then(done).catch(done).then(callback)
        })
    }

    restoreLayout = () => {
        const { client, withLoader, functionName } = this.props
        return withLoader(done => {
            return client.delete(gridLayoutEndpoint(functionName)).then(() => {
                return this.setState(({ config }) => ({
                    layout: {},
                    grid: ({ ...config, columns: [...config.columns] })
                }))
            }).then(done).catch(done)
        })
    }

    onEdit = ({ row, field, value }) => {
        const { client, withLoader, editEndpoint } = this.props
        return editEndpoint && withLoader(done => {
            return client.post(editEndpoint, { ...row, [field]: value }).then(() => {
                return setTimeout(Toast.toast({
                    type: 'success',
                    showCloseButton: false,
                    text: `Row updated.`
                }), 1000)
            }).then(done).catch(done)
        })
    }

    saveAddedRows = () => {
        const { client, withLoader, editEndpoint } = this.props
        const addedRows = this.gridRef.current.getAddedRows()

        if (!addedRows.length) return

        return withLoader(done => {
            return Promise.all(addedRows.map(row => {
                return client.post(editEndpoint, { ...row }, { ignoreError: true }).catch((error) => ({ error, row }))
            })).then(async (res) => {
                const errored = res.some(i => i.error)
                if (errored) {
                    Toast.toast({
                        type: 'error',
                        showCloseButton: true,
                        text: await Promise.all(res.map(res => res.error ? getErrorMessage(res.error) : '')).join('\n')
                    }, { overlay: true })
                }
                else {
                    setTimeout(Toast.toast({
                        type: 'success',
                        showCloseButton: false,
                        text: `Row saved.`
                    }), 1000)
                }
            }).then(done).then(this.loadData).catch(done)
        })
    }

    onDoubleClickRow = ({ row }) => {

        if (!row) return

        const { onDoubleClickRow, childPath } = this.props


        if (onDoubleClickRow) {
            return onDoubleClickRow(row, this.loadData)
        }
        else if (childPath) {
            const { history } = this.props
            const templateParameters = childPath.match(/\{\{(.+?)\}\}/g).map(s => s.match(/^\{\{(.+?)\}\}/)[1])

            if (!templateParameters.some(param => typeof row[param] == 'undefined')) {
                return history.push({ pathname: template(childPath, row), state: { onSave: this.props.loadData || this.loadData } })
            }
        }
    }

    onClickRow = ({ row, rowIndex }) => {

        if (this.props.onClickRow) {
            return this.props.onClickRow(row, this.loadData)
        }

        return this.selectedRow = { row, rowIndex }
    }

    getSelectedRow = () => {
        return this.selectedRow
    }

    replaceDetail = memoize((columns = []) => {

        const { showDataList } = this.props

        if (showDataList) {
            return columns.filter(column => column.key != 'detail')
        }

        return columns.map(column => {
            if (column.key == 'detail' && column.dataType == 'link' && this.props.childPath) {
                return { ...column, format: { ...column.format, to: { pathname: this.props.childPath, state: { onSave: this.props.loadData || this.loadData } }, relative: false } }
            }
            return column
        })
    }, { maxSize: 1 })

    renderActions = (originalGridActions) => {
        const { config = {}, layout = {} } = this.state
        const { actions: { grid: gridActions = [] } = { grid: [] }, editEndpoint } = this.props

        const editable = _.get(this.state, 'config.editable', {})
        const inlineAdd = editable === true || editable.add === true

        if (isMobile && !gridActions.length) return null

        const actions = [
            ...gridActions,
            inlineAdd && editEndpoint && {
                title: 'Save',
                tooltip: 'Save',
                icon: 'save',
                onClick: this.saveAddedRows
            }
        ].filter(Boolean)

        return (
            <Flex row flex={isMobile ? 0 : 1} justifyBetween ph={10} pv={isMobile ? 10 : 0}>
                <Flex row flex={isMobile ? 1 : 0} justifyStart={!isMobile} justifyEvenly={isMobile} alignCenter>
                    {actions.map(({ title, active, onClick, ...action }, index) => (
                        <Button
                            key={index}
                            ph={isMobile ? 8 : 10}
                            pv={isMobile ? 2 : 5}
                            {...action}
                            outline={isMobile}
                            title={isMobile ? title : undefined}
                            tooltip={title}
                            onClick={onClick && onClick.bind(this)}
                        />
                    ))}
                </Flex>
                {isMobile ? null : (
                    <Flex row alignEnd>
                        <DropdownMenu buttonProps={{ pv: 5, ph: 10 }} bottom center marginTop={10} icon={'table_edit'} actions={[
                            {
                                title: 'Edit layout',
                                icon: 'table_eye',
                                onClick: () => {

                                    const columns = config.columns.filter(c => !c.hidden)
                                    const [{ columnsOrder }] = this.gridRef.current.getState()

                                    const sortedColumns = !columnsOrder ? columns : columns.sort((a, b) => {
                                        return columnsOrder.indexOf(a.key) - columnsOrder.indexOf(b.key)
                                    })

                                    return GridLayout({
                                        layout,
                                        columns: sortedColumns,
                                        onSave: this.applyLayout
                                    })
                                }
                            },
                            {
                                title: 'Save layout',
                                icon: 'table_save',
                                onClick: this.saveLayout
                            },
                            {
                                title: 'Restore layout',
                                icon: 'table_restore',
                                onClick: this.restoreLayout
                            }
                        ]}
                        />
                    </Flex>
                )}
            </Flex>
        )
    }

    renderAccessory = (row) => {
        const { grid: { columns = [] } = {} } = this.state
        const detailColumn = columns.find(column => column.key == 'detail')

        if (!detailColumn) return null

        const buttonProps = {
            ...detailColumn.format,
            to: {
                pathname: this.props.childPath,
                state: { onSave: this.props.loadData || this.loadData }
            },
            relative: false
        }
        return (
            <Flex fill alignCenter justifyCenter width={10}>
                <Button {...buttonProps} icon={'navigate_next'} data={row} />
            </Flex>
        )
    }

    render() {
        const { showSort = false, showDataList = false, title } = this.props
        const { grid = {}, rows = [], total, grid: { editable = false } } = this.state

        const subtitle = total && isFinite(total) ? `Showing {{rowCount}} of ${total}` : undefined

        const DataComponent = showDataList ? DataList : DataGrid

        const header = isMobile
            ? false
            : {
                ...grid.header,
                ...title && { title },
                ...subtitle && { subtitle },
            }

        return (
            <Flex column fill flex={1}>
                <DataComponent
                    {...grid}
                    itemHeight={24}
                    ref={this.gridRef}
                    rows={rows}
                    showSort={showDataList && showSort}
                    sortSelectProps={sortSelectProps}
                    columns={this.replaceDetail(grid.columns)}
                    onClickRow={this.onClickRow}
                    onDoubleClickRow={this.onDoubleClickRow}
                    renderActions={this.renderActions}
                    renderAccessory={this.renderAccessory}
                    disableEditableSelect
                    header={header}
                    onEdit={editable && (editable == true || editable.edit == true) && this.onEdit}
                />
                <Container>
                    {isMobile ? this.renderActions() : null}
                </Container>
            </Flex>
        )

    }
}


export default withDashboardContext(withRouter(GridTemplate), ({ client, withLoader }) => ({ client, withLoader }))
